import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Seo } from "@/components/Seo";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radio, Bell, MessageSquare, Signal, ArrowUpRight, Droplets, Package, Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const KpiCard = ({ icon: Icon, label, value, sub, tone = "blue", testid }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    rose: "bg-rose-50 text-rose-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };
  return (
    <Card data-testid={testid} className="border-slate-200 p-5 shadow-none">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</span>
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${tones[tone]}`}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>
      <div className="mt-3 font-mono text-3xl font-light tracking-tight text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-400">{sub}</div>
    </Card>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [stations, setStations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [featured, setFeatured] = useState([]);

  const load = async () => {
    const [s, st, al] = await Promise.all([
      api.get("/stats"),
      api.get("/stations"),
      api.get("/alerts?limit=6"),
    ]);
    setStats(s.data);
    setStations(st.data);
    setAlerts(al.data);
  };

  useEffect(() => {
    api.get(`/products?lang=${lang}`).then(({ data }) => setFeatured(data.filter((p) => p.featured).slice(0, 6))).catch(() => {});
  }, [lang]);

  useEffect(() => {
    load();
    const tm = setInterval(load, 15000);
    return () => clearInterval(tm);
  }, []);

  const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString() : t("stations.noSignal"));

  return (
    <Layout>
      <Seo title={t("seo.dashTitle")} description={t("seo.dashDesc")} path="/" />
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")} />
      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard testid="kpi-stations" icon={Radio} label={t("dashboard.totalStations")}
            value={stats?.total_stations ?? "—"}
            sub={`${stats?.online_stations ?? 0} ${t("dashboard.online")}`} tone="blue" />
          <KpiCard testid="kpi-alarms" icon={Bell} label={t("dashboard.activeAlarms")}
            value={stats?.active_alarms ?? "—"} sub={t("dashboard.last24h")} tone="rose" />
          <KpiCard testid="kpi-messages" icon={MessageSquare} label={t("dashboard.pendingMessages")}
            value={stats?.pending_messages ?? "—"} sub={t("dashboard.inQueue")} tone="amber" />
          <KpiCard testid="kpi-rssi" icon={Signal} label={t("dashboard.avgSignal")}
            value={stats?.avg_rssi != null ? `${stats.avg_rssi} dBm` : "—"} sub={t("dashboard.avgRssi")} tone="emerald" />
        </div>

        {featured.length > 0 && (
          <Card className="border-slate-200 p-5 shadow-none" data-testid="featured-products">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <Star className="h-4 w-4 text-blue-600" strokeWidth={2} /> {t("dashboard.featured")}
              </h3>
              <button onClick={() => navigate("/productos")}
                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                {t("common.viewAll")} <ArrowUpRight className="h-3 w-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {featured.map((p) => (
                <Link key={p.id} to={`/p/${p.id}`} data-testid={`featured-${p.id}`}
                  className="flex flex-col overflow-hidden rounded-lg border border-slate-200 transition-shadow hover:shadow-md">
                  <div className="flex h-20 items-center justify-center bg-white">
                    {p.images && p.images.length > 0 ? (
                      <img src={`${API}/api/public/products/${p.id}/image/${p.images[0].id}`} alt={p.name} className="h-full w-full object-contain p-2" />
                    ) : (
                      <Package className="h-6 w-6 text-slate-300" />
                    )}
                  </div>
                  <div className="p-2">
                    {p.is_new && <Badge className="mb-1 bg-emerald-500 text-[10px] text-white hover:bg-emerald-500">{t("products.badgeNew")}</Badge>}
                    <span className="block truncate text-xs font-medium text-slate-700">{p.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="border-slate-200 p-5 shadow-none lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">{t("dashboard.stationStatus")}</h3>
              <button onClick={() => navigate("/stations")}
                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                {t("common.viewAll")} <ArrowUpRight className="h-3 w-3" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {stations.map((s) => (
                <button key={s.id} data-testid={`station-card-${s.code}`}
                  onClick={() => navigate(`/stations/${s.id}`)}
                  className="rounded-lg border border-slate-200 p-4 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/40">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-50">
                        <Droplets className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
                      </div>
                      <div className="leading-tight">
                        <div className="text-sm font-medium text-slate-900">{s.name}</div>
                        <div className="font-mono text-xs text-slate-400">{s.code}</div>
                      </div>
                    </div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <span className="font-mono">{s.rssi != null ? `${s.rssi} dBm` : t("stations.noSignal")}</span>
                    <span>{fmtTime(s.last_seen)}</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          <Card className="border-slate-200 p-5 shadow-none">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">{t("dashboard.recentAlarms")}</h3>
              <button onClick={() => navigate("/alerts")}
                className="text-xs font-medium text-blue-600 hover:text-blue-700">{t("common.viewAll")}</button>
            </div>
            <div className="space-y-3">
              {alerts.length === 0 && <p className="text-sm text-slate-400">{t("dashboard.noRecentAlarms")}</p>}
              {alerts.map((a) => (
                <div key={a.id} className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0">
                  <div className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.severity === "critical" ? "bg-rose-500" : "bg-amber-500"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">{a.reason}</div>
                    <div className="text-xs text-slate-400">{a.code} • {t("dashboard.channel")} {a.channel}</div>
                    <div className="mt-0.5 text-xs text-slate-400">{fmtTime(a.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
